(function () {
  const CUSTOM_ELEMENT_NAME = 'ag-header-meta-row';
  const SVG_NAMESPACE = 'http://www.w3.org/2000/svg';
  const SVG_CLASS = 'header__ag-meta-svg';
  const READY_CLASS = 'header__ag-right--meta-ready';
  const ACTIVE_CLASS = 'header__ag-action--meta-active';
  const MEDIA_QUERY = '(min-width: 1024px) and (hover: hover) and (pointer: fine)';
  const TARGET_SELECTORS = ['.header__ag-nav', '.header__ag-action--cart', '.header__ag-account'];
  const ACTION_TARGET_SELECTOR = '.header__ag-action--cart, .header__ag-account';
  const SUPPORTED_POINTER_TYPES = ['mouse', 'pen'];
  const MIN_TARGET_COUNT = 2;
  const MIN_VISIBLE_SIZE = 0;
  const MAX_CORNER_RADIUS = 32;
  const MIN_BRIDGE_GAP = -0.5;
  const MAX_BRIDGE_GAP = 42;
  const BRIDGE_HALF_HEIGHT_RATIO = 0.24;
  const BRIDGE_CONTROL_Y_RATIO = 0.105;
  const BRIDGE_SEAM_OVERLAP = 1;
  const MIN_BRIDGE_CONTROL = 1;
  const BRIDGE_CONTROL_RATIO = 0.09;
  const ACTION_HALO_MIN_VERTICAL_PADDING = 18;
  const ACTION_HALO_VERTICAL_PADDING_RATIO = 0.34;
  const ACTIVE_HOLD_MIN_HORIZONTAL_PADDING = 30;
  const ACTIVE_HOLD_HORIZONTAL_PADDING_RATIO = 0.34;
  const ACTIVE_HOLD_MIN_VERTICAL_PADDING = 34;
  const ACTIVE_HOLD_VERTICAL_PADDING_RATIO = 0.62;
  const BRIDGE_HIT_MIN_VERTICAL_PADDING = 28;
  const BRIDGE_HIT_VERTICAL_PADDING_RATIO = 0.5;
  const BRIDGE_HIT_HORIZONTAL_PADDING = 8;
  const MOTION_SETTLE_MS = 400;

  class AgHeaderMetaRow extends HTMLElement {
    constructor() {
      super();
      this.media = window.matchMedia(MEDIA_QUERY);
      this.frame = null;
      this.resizeObserver = null;
      this.targets = [];
      this.pointer = null;
      this.activeTarget = null;
      this.isInitialized = false;

      this.handleMediaChange = this.handleMediaChange.bind(this);
      this.handlePointerMove = this.handlePointerMove.bind(this);
      this.clearPointerActive = this.clearPointerActive.bind(this);
      this.loop = this.loop.bind(this);
      this.start = this.start.bind(this);
    }

    connectedCallback() {
      if (this.isInitialized) {
        return;
      }

      this.isInitialized = true;
      this.createSvg();
      this.media.addEventListener('change', this.handleMediaChange);
      this.handleMediaChange();
    }

    disconnectedCallback() {
      if (!this.isInitialized) {
        return;
      }

      this.media.removeEventListener('change', this.handleMediaChange);
      this.disable();
      this.isInitialized = false;
    }

    createSvg() {
      if (this.svg) {
        return;
      }

      this.svg = document.createElementNS(SVG_NAMESPACE, 'svg');
      this.svg.classList.add(SVG_CLASS);
      this.svg.setAttribute('aria-hidden', 'true');
      this.svg.setAttribute('focusable', 'false');
      this.svg.setAttribute('preserveAspectRatio', 'none');

      this.bridgePath = document.createElementNS(SVG_NAMESPACE, 'path');
      this.rectPath = document.createElementNS(SVG_NAMESPACE, 'path');

      this.svg.append(this.bridgePath, this.rectPath);
      this.prepend(this.svg);
    }

    handleMediaChange() {
      if (this.media.matches) {
        this.enable();
        return;
      }

      this.disable();
    }

    enable() {
      this.targets = this.getTargets();

      if (this.targets.length < MIN_TARGET_COUNT) {
        this.disable();
        return;
      }

      this.classList.add(READY_CLASS);

      if (!this.resizeObserver) {
        this.resizeObserver = new ResizeObserver(this.start);
      }

      this.resizeObserver.observe(this);
      this.targets.forEach((target) => this.resizeObserver.observe(target));
      document.addEventListener('pointermove', this.handlePointerMove);
      document.addEventListener('pointerleave', this.clearPointerActive);
      window.addEventListener('blur', this.clearPointerActive);
      window.addEventListener('scroll', this.start, { passive: true });
      this.addEventListener('focusin', this.start);
      this.addEventListener('focusout', this.start);
      this.start();
    }

    disable() {
      this.classList.remove(READY_CLASS);
      this.clearPointerActive();
      this.stop();
      document.removeEventListener('pointermove', this.handlePointerMove);
      document.removeEventListener('pointerleave', this.clearPointerActive);
      window.removeEventListener('blur', this.clearPointerActive);
      window.removeEventListener('scroll', this.start);
      this.removeEventListener('focusin', this.start);
      this.removeEventListener('focusout', this.start);

      if (this.resizeObserver) {
        this.resizeObserver.disconnect();
      }

      this.bridgePath.setAttribute('d', '');
      this.rectPath.setAttribute('d', '');
    }

    getTargets() {
      return TARGET_SELECTORS.map((selector) => this.querySelector(selector)).filter(
        (target) => target && this.isVisible(target)
      );
    }

    isVisible(target) {
      const rect = target.getBoundingClientRect();
      return rect.width > MIN_VISIBLE_SIZE && rect.height > MIN_VISIBLE_SIZE;
    }

    start() {
      this.motionUntil = performance.now() + MOTION_SETTLE_MS;
      if (!this.frame) {
        this.frame = requestAnimationFrame(this.loop);
      }
    }

    stop() {
      if (this.frame) {
        cancelAnimationFrame(this.frame);
        this.frame = null;
      }
    }

    loop() {
      this.frame = null;
      this.syncPointerActive();
      this.update();
      if (performance.now() < this.motionUntil) {
        this.frame = requestAnimationFrame(this.loop);
      }
    }

    handlePointerMove(event) {
      if (event.pointerType && !SUPPORTED_POINTER_TYPES.includes(event.pointerType)) {
        this.clearPointerActive();
        return;
      }

      this.pointer = {
        x: event.clientX,
        y: event.clientY,
      };
      this.syncPointerActive();
      this.start();
    }

    clearPointerActive() {
      this.pointer = null;
      this.setActiveTarget(null);
      if (this.media.matches && this.isInitialized) this.start();
    }

    syncPointerActive() {
      if (!this.pointer || !this.media.matches) {
        return;
      }

      this.setActiveTarget(this.targetFromPoint(this.pointer.x, this.pointer.y));
    }

    setActiveTarget(activeTarget) {
      this.activeTarget = activeTarget;
      this.targets.forEach((target) => {
        target.classList.toggle(ACTIVE_CLASS, target === activeTarget);
      });
    }

    targetFromPoint(clientX, clientY) {
      const items = this.targets
        .filter((target) => this.isVisible(target))
        .map((target) => ({
          target,
          rect: target.getBoundingClientRect(),
        }))
        .sort((a, b) => a.rect.left - b.rect.left);

      const directMatch = items.find(({ rect }) => this.pointInRect(clientX, clientY, rect));

      if (directMatch) {
        return this.isActionTarget(directMatch.target) ? directMatch.target : null;
      }

      const activeItem = items.find(({ target }) => target === this.activeTarget);

      if (activeItem && this.pointInActiveHold(clientX, clientY, activeItem.rect)) {
        return this.activeTarget;
      }

      const actionMatch = items.find(
        ({ target, rect }) => this.isActionTarget(target) && this.pointInActionHalo(clientX, clientY, rect)
      );

      if (actionMatch) {
        return actionMatch.target;
      }

      for (let index = 0; index < items.length - 1; index += 1) {
        const left = items[index];
        const right = items[index + 1];

        if (this.pointInBridge(clientX, clientY, left.rect, right.rect)) {
          if (this.activeTarget === left.target || this.activeTarget === right.target) {
            return this.activeTarget;
          }

          return this.isActionTarget(right.target) ? right.target : left.target;
        }
      }

      return null;
    }

    isActionTarget(target) {
      return target.matches(ACTION_TARGET_SELECTOR);
    }

    pointInRect(x, y, rect) {
      return x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom;
    }

    pointInActionHalo(x, y, rect) {
      const verticalPadding = Math.max(
        ACTION_HALO_MIN_VERTICAL_PADDING,
        rect.height * ACTION_HALO_VERTICAL_PADDING_RATIO
      );

      return x >= rect.left && x <= rect.right && y >= rect.top - verticalPadding && y <= rect.bottom + verticalPadding;
    }

    pointInActiveHold(x, y, rect) {
      const horizontalPadding = Math.max(
        ACTIVE_HOLD_MIN_HORIZONTAL_PADDING,
        rect.width * ACTIVE_HOLD_HORIZONTAL_PADDING_RATIO
      );
      const verticalPadding = Math.max(
        ACTIVE_HOLD_MIN_VERTICAL_PADDING,
        rect.height * ACTIVE_HOLD_VERTICAL_PADDING_RATIO
      );

      return (
        x >= rect.left - horizontalPadding &&
        x <= rect.right + horizontalPadding &&
        y >= rect.top - verticalPadding &&
        y <= rect.bottom + verticalPadding
      );
    }

    update() {
      const rootRect = this.getBoundingClientRect();
      const rects = this.targets
        .filter((target) => this.isVisible(target))
        .map((target) => {
          const rect = target.getBoundingClientRect();

          return {
            x: rect.left - rootRect.left,
            y: rect.top - rootRect.top,
            width: rect.width,
            height: rect.height,
          };
        })
        .sort((a, b) => a.x - b.x);

      if (!rects.length) {
        return;
      }

      const setAttribute = (element, name, value) => {
        if (element.getAttribute(name) !== value) element.setAttribute(name, value);
      };
      setAttribute(this.svg, 'viewBox', `0 0 ${rootRect.width} ${rootRect.height}`);
      setAttribute(this.rectPath, 'd', rects.map((rect) => this.roundedRect(rect)).join(' '));
      setAttribute(this.bridgePath, 'd', this.bridges(rects, this.activeIndex()));
    }

    roundedRect(rect) {
      const radius = Math.min(rect.height / 2, MAX_CORNER_RADIUS);
      const right = rect.x + rect.width;
      const bottom = rect.y + rect.height;

      return [
        `M${rect.x + radius} ${rect.y}`,
        `L${right - radius} ${rect.y}`,
        `A${radius} ${radius} 0 0 1 ${right} ${rect.y + radius}`,
        `L${right} ${bottom - radius}`,
        `A${radius} ${radius} 0 0 1 ${right - radius} ${bottom}`,
        `L${rect.x + radius} ${bottom}`,
        `A${radius} ${radius} 0 0 1 ${rect.x} ${bottom - radius}`,
        `L${rect.x} ${rect.y + radius}`,
        `A${radius} ${radius} 0 0 1 ${rect.x + radius} ${rect.y}`,
        'Z',
      ].join(' ');
    }

    activeIndex() {
      return this.targets.findIndex(
        (target) =>
          this.isActionTarget(target) &&
          (target.classList.contains(ACTIVE_CLASS) ||
            target.matches(':hover') ||
            target.matches(':focus-visible') ||
            target.matches(':focus-within'))
      );
    }

    bridges(rects, activeIndex) {
      if (activeIndex < 0) {
        return '';
      }

      const paths = [];

      if (activeIndex > 0) {
        paths.push(this.bridge(rects[activeIndex - 1], rects[activeIndex]));
      }

      if (activeIndex < rects.length - 1) {
        paths.push(this.bridge(rects[activeIndex], rects[activeIndex + 1]));
      }

      return paths.join(' ');
    }

    bridge(left, right) {
      const gap = right.x - (left.x + left.width);
      const height = Math.min(left.height, right.height);

      if (gap < MIN_BRIDGE_GAP || gap > MAX_BRIDGE_GAP) {
        return '';
      }

      const leftEdge = left.x + left.width;
      const rightEdge = right.x;
      const middleY = (Math.max(left.y, right.y) + Math.min(left.y + left.height, right.y + right.height)) / 2;
      const topY = middleY - height * BRIDGE_HALF_HEIGHT_RATIO;
      const bottomY = middleY + height * BRIDGE_HALF_HEIGHT_RATIO;
      const topControlY = middleY - height * BRIDGE_CONTROL_Y_RATIO;
      const bottomControlY = middleY + height * BRIDGE_CONTROL_Y_RATIO;
      const leftOvershoot = this.bridgeOvershoot(left, topY, bottomY);
      const rightOvershoot = this.bridgeOvershoot(right, topY, bottomY);
      const x1 = leftEdge - leftOvershoot;
      const x2 = rightEdge + rightOvershoot;
      const control = Math.max(MIN_BRIDGE_CONTROL, gap * BRIDGE_CONTROL_RATIO);

      return [
        `M${x1} ${bottomY}`,
        `C${x1 + control} ${bottomControlY} ${x2 - control} ${bottomControlY} ${x2} ${bottomY}`,
        `L${x2} ${topY}`,
        `C${x2 - control} ${topControlY} ${x1 + control} ${topControlY} ${x1} ${topY}`,
        'Z',
      ].join(' ');
    }

    bridgeOvershoot(rect, topY, bottomY) {
      return (
        Math.max(this.roundedSideInset(rect, topY), this.roundedSideInset(rect, bottomY)) +
        BRIDGE_SEAM_OVERLAP
      );
    }

    roundedSideInset(rect, y) {
      const radius = Math.min(rect.height / 2, MAX_CORNER_RADIUS);
      const topArcCenterY = rect.y + radius;
      const bottomArcCenterY = rect.y + rect.height - radius;

      if (y >= topArcCenterY && y <= bottomArcCenterY) {
        return 0;
      }

      const arcCenterY = y < topArcCenterY ? topArcCenterY : bottomArcCenterY;
      const verticalDistance = Math.min(radius, Math.abs(y - arcCenterY));

      return radius - Math.sqrt(radius ** 2 - verticalDistance ** 2);
    }

    pointInBridge(x, y, left, right) {
      const gap = right.left - left.right;
      const height = Math.min(left.height, right.height);

      if (gap < MIN_BRIDGE_GAP || gap > MAX_BRIDGE_GAP) {
        return false;
      }

      const middleY = (Math.max(left.top, right.top) + Math.min(left.bottom, right.bottom)) / 2;
      const verticalPadding = Math.max(
        BRIDGE_HIT_MIN_VERTICAL_PADDING,
        height * BRIDGE_HIT_VERTICAL_PADDING_RATIO
      );
      const topY = middleY - height * BRIDGE_HALF_HEIGHT_RATIO - verticalPadding;
      const bottomY = middleY + height * BRIDGE_HALF_HEIGHT_RATIO + verticalPadding;

      return (
        x >= left.right - BRIDGE_HIT_HORIZONTAL_PADDING &&
        x <= right.left + BRIDGE_HIT_HORIZONTAL_PADDING &&
        y >= topY &&
        y <= bottomY
      );
    }
  }

  if (!customElements.get(CUSTOM_ELEMENT_NAME)) {
    customElements.define(CUSTOM_ELEMENT_NAME, AgHeaderMetaRow);
  }
})();
